/*
 * Copyright (c) 2024 Ask Sage, Inc. All rights reserved.
 * This software is the confidential and proprietary information of Ask Sage, Inc.
 * You shall not disclose such confidential information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Ask Sage, Inc.
 * THIS SOFTWARE IS PROVIDED "AS IS" AND ANY EXPRESSED OR IMPLIED WARRANTIES,
 * INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND
 * FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL ASK SAGE, INC.
 * OR ITS CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL,
 * EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT
 * OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE, DATA, OR PROFITS; OR BUSINESS
 * INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT,
 * STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY
 * OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
 * You may not use this software except in compliance with the License.
 * You may not modify, reverse engineer, or redistribute this software.
 */
import React, { useState, useEffect } from 'react';
import * as default_data from '../../config.js'
import { getToken } from '../../AuthService.js';
import { Box, Button, Typography, TextField, useTheme } from '@mui/material';
import { CustomDialog } from '../../Components/CustomDialog/CustomDialog.js';
import { SelectClassification } from '../../Utils/classification_helper.js';

const AddWorkbook = ({ loadWorkbooks, triggerAddWorkbook, setTriggerAddWorkbook }) => {
  const theme = useTheme();

  const [loaded, setLoaded] = useState(false);
  var loaded_static = false;

  const [modalIsOpen, setIsOpen] = React.useState(false);
  const [title, setTitle] = useState('');
  const [classification, setClassification] = useState(default_data.default_allowed_classifications[0]);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = event => {
    event.preventDefault();
    setIsLoading(true);

    var token = getToken()
    if(token === false) {
      return;
    }

    fetch(default_data.default_user_service_url_no_cac + '/workbook', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'x-access-tokens': `${token}`
      },
      body: JSON.stringify({
        title: title,
        classification: classification
      })
    })
      .then(response => {
        if (response.ok) {
          return response.json();
        }
        throw new Error('An error occurred while submitting.');
      })
      .then(data => {
        if(data.status != 200) {
          throw new Error(data.response);
        }
        setIsLoading(false);
        // Clear the form and display a success message
        setTitle('');
        setErrorMessage('');

        loadWorkbooks()
        setSuccessMessage("Your workbook templated has been added!");
        const sleep = async (milliseconds) => {
          await new Promise(resolve => {
              return setTimeout(resolve, milliseconds)
          });
        };
        const run_sleep = async () => {
          closeModal()
          setSuccessMessage('')
        }
        run_sleep();
      })
      .catch(error => {
        setIsLoading(false);
        setErrorMessage(error.message);
      });
  };

  function closeModal() {
    setTriggerAddWorkbook(0);
    setIsOpen(false);
  }

  useEffect(() => {
    if (triggerAddWorkbook) {
      if (triggerAddWorkbook === 1) {
        setIsOpen(true)
        // clear the old state
        setTitle('')
        setClassification(default_data.default_allowed_classifications[0])
        setErrorMessage('')
        setSuccessMessage('')
      }
    }
  }, [triggerAddWorkbook]);

  useEffect(() => {

    if(loaded || loaded_static) {
      return;
    }

    setLoaded(true)
    loaded_static = true;

    }, [])

    const handleChangeClassification = (event) => {
      setClassification(event.target.value);
    };

  return (
    <CustomDialog
      open={modalIsOpen}
      onClose={closeModal}
      maxWidth={'md'}
      fullWidth={true}
      title={'Add Workbook'}
      allowEscape={true}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <Typography variant="body2" color="textPrimary">
        Enter the title of the workbook you would like to create.
      </Typography>
      <TextField
        fullWidth
        size='small'
        variant="outlined"
        value={title}
        onChange={e => setTitle(e.target.value)}
        error={((title.length) > 100)}
        helperText={((title.length) > 100) ? 'Title is too long' : `(${title.length}/100)`}
      />
      <Box display="flex" alignItems="center">
        <SelectClassification value={classification} onChange={handleChangeClassification} />
      </Box> 
      {errorMessage && <p style={{ color: 'red' }}>{errorMessage}</p>}
      {successMessage && <p style={{ color: 'green' }}>{successMessage}</p>}
      <Box display="flex" justifyContent="flex-end">
        <Button 
          onClick={closeModal} 
          variant='text' 
          disabled={isLoading} 
          color={theme.palette.text.primary}
          sx={{ 
            marginRight: 2,
            fontWeight: 'bold',
            cursor: 'pointer',
          }}
        >  
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          disabled={isLoading || (title.length === 0) || ((title.length) > 100)} 
          variant='contained'
          sx={{
            fontWeight: 'bold',
            cursor: 'pointer',
          }}
        >
          {isLoading ? 'Loading...' : 'Submit'}
        </Button>
      </Box>
    </CustomDialog>
  );
}
export default AddWorkbook;
